import type { App } from 'vue';
import type { I18n, I18nOptions } from 'vue-i18n';

import { createI18n } from 'vue-i18n';
import { setHtmlPageLang, setLoadLocalePool } from './helper';
import { localeSetting } from '/@/settings/localeSetting';
import { useLocaleStoreWithOut } from '/@/store/modules/locale';

const { fallback, availableLocales } = localeSetting;

export let i18n: ReturnType<typeof createI18n>;

// Locale map to statically handle imports
const localeMap: Record<string, () => Promise<any>> = {
  en: () => import('/@/locales/lang/en.ts'),

};

async function createI18nOptions(): Promise<I18nOptions> {
  const localeStore = useLocaleStoreWithOut();
  const locale = localeStore.getLocale;

  // Import the correct locale file or fallback to 'en'
  const defaultLocal = localeMap[locale] ? await localeMap[locale]() : await localeMap['en']();
  const message = defaultLocal.default?.message ?? {};

  // Set the language for the HTML document
  setHtmlPageLang(locale);

  // Add the locale to the loaded locale pool
  setLoadLocalePool((loadLocalePool) => {
    loadLocalePool.push(locale);
  });

  return {
    legacy: false,
    locale,
    fallbackLocale: fallback,
    messages: {
      [locale]: message,
    },
    availableLocales: availableLocales,
    sync: true, // Prevents inheriting the global scope locale if set to false
    silentTranslationWarn: true, // Set to true to suppress warnings
    missingWarn: false, // Turn off warnings for missing translations
    silentFallbackWarn: true, // Suppress fallback warnings
  };
}

// Setup the i18n instance with the global app
export async function setupI18n(app: App) {
  const options = await createI18nOptions();
  i18n = createI18n(options) as I18n;
  app.use(i18n);
}
